import { useRef, useState, useCallback } from "react";
import { Upload, FileText, CheckCircle2, Trash2, Hash, ChevronDown } from "lucide-react";

const MAX_FILE_SIZE_MB = 5;

const QUOTE_DOCUMENT_TYPE_OPTIONS = [
  { value: "Renewal Business Quote", label: "Renewal Business Quote" },
  { value: "New Business Quote", label: "New Business Quote" },
  { value: "Motor Transfer Documents", label: "Motor Transfer Documents" },
];

const CPR_DOCUMENT_TYPE_OPTIONS = [
  { value: "Proposal Form", label: "Proposal Form" },
  { value: "General Document", label: "General Document" },
];

export function UploadPdf({
  selectedFile,
  onFileSelect,
  onFileClear,
  referenceNumber,
  onReferenceChange,
  referenceType,
  onReferenceTypeChange,
  documentType,
  onDocumentTypeChange,
  customer,
  onCustomerChange,
  maxSizeMB = MAX_FILE_SIZE_MB,
}) {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState(null);

  const validate = useCallback(
    (file) => {
      if (!file) return;
      if (file.type !== "application/pdf") {
        setError("PDF only");
        onFileClear();
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`Max ${maxSizeMB}MB`);
        onFileClear();
        return;
      }
      setError(null);
      onFileSelect(file);
    },
    [maxSizeMB, onFileClear, onFileSelect],
  );

  const handleCustomerChange = (key, value) => {
    onCustomerChange({
      ...customer,
      [key]: value,
    });
  };

  const clearDocument = () => {
    setError(null);
    onFileClear();
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  return (
    <div className="mx-auto max-w-xl">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 space-y-6">
        <h2 className="text-lg font-black text-slate-800">Upload Document</h2>

        <div className="space-y-3">
          <label className="block text-sm font-bold text-slate-700">
            Reference Type <span className="text-red-500">*</span>
          </label>

          <div className="flex gap-4">
            {[
              { value: "Quote", label: "Quote/Policy" },
              { value: "CPR", label: "CPR/ID" },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition
                  ${
                    referenceType === opt.value
                      ? "border-sky-500 bg-sky-50 text-sky-700"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
              >
                <input
                  type="radio"
                  name="referenceType"
                  value={opt.value}
                  checked={referenceType === opt.value}
                  onChange={(e) => onReferenceTypeChange(e.target.value)}
                  className="accent-sky-500"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {referenceType && (
          <div className="space-y-1">
            <label className="block text-sm font-bold text-slate-700">
              {referenceType === "CPR" ? "CPR/ID" : "Quote/Policy"}{" "}
              <span className="text-red-500">*</span>
            </label>

            <div className="relative">
              <Hash
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => onReferenceChange(e.target.value)}
                placeholder={`Enter ${referenceType === "CPR" ? "CPR/ID" : "Quote/Policy"}`}
                className="w-full rounded-full border border-slate-300
                  pl-11 pr-4 py-3 text-sm font-semibold
                  focus:border-sky-500 focus:ring-2 focus:ring-sky-200
                  outline-none transition"
              />
            </div>
          </div>
        )}

        <div className="space-y-1">
          <label className="block text-sm font-bold text-slate-700">
            Document Type <span className="text-red-500">*</span>
          </label>

          <div className="relative">
            <ChevronDown
              size={18}
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <select
              value={documentType}
              onChange={(e) => onDocumentTypeChange(e.target.value)}
              className="w-full appearance-none rounded-full border border-slate-300
                px-4 py-3 text-sm font-semibold
                focus:border-sky-500 focus:ring-2 focus:ring-sky-200
                outline-none transition bg-white"
            >
              <option value="" disabled>
                Select document type
              </option>
              {(referenceType === "CPR" ? CPR_DOCUMENT_TYPE_OPTIONS : QUOTE_DOCUMENT_TYPE_OPTIONS).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-black text-slate-700">
            Customer Details
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={customer.customerFirstName}
              onChange={(v) => handleCustomerChange("customerFirstName", v)}
              placeholder="John"
              required
            />
            <Input
              label="Last Name"
              value={customer.customerLastName}
              onChange={(v) => handleCustomerChange("customerLastName", v)}
              placeholder="Doe"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={customer.customerEmail}
              onChange={(v) => handleCustomerChange("customerEmail", v)}
              placeholder="john@example.com"
              required
            />
            <Input
              label="Phone"
              type="tel"
              value={customer.customerPhone}
              onChange={(v) => handleCustomerChange("customerPhone", v)}
              placeholder="+1234567890"
              required
            />
          </div>
        </div>

        {!selectedFile && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              validate(e.dataTransfer.files?.[0]);
            }}
            className={`rounded-2xl border-2 p-6 text-center transition
              ${drag ? "border-sky-500 scale-[1.02]" : "border-sky-200"}`}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              hidden
              onChange={(e) => validate(e.target.files?.[0])}
            />

            <h3 className="text-lg font-bold">Upload PDF</h3>
            <p className="mt-1 text-sm text-slate-500">
              Drag & drop or click below
            </p>

            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="mt-4 inline-flex items-center gap-2 rounded-full
                bg-sky-500 px-5 py-2 text-sm font-semibold text-white
                hover:bg-sky-600 active:scale-95"
            >
              <Upload size={14} />
              Choose file
            </button>
          </div>
        )}

        {selectedFile && (
          <div className="relative rounded-xl bg-white border border-slate-200 p-1 shadow-md">
            <div className="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-sky-500" />

            <div className="flex items-center justify-between gap-4 pl-3">
              <div className="flex items-center gap-4 min-w-0">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                  <FileText />
                </div>

                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <CheckCircle2 size={12} className="text-green-500" />
                    Ready • {(selectedFile.size / 1e6).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={clearDocument}
                className="h-9 w-9 rounded-full flex items-center justify-center
               text-slate-400 hover:text-red-500 hover:bg-red-50 transition"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="text-center text-sm font-black text-red-600 bg-red-50 p-3 rounded-xl">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-bold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-full border border-slate-300
          px-4 py-3 text-sm font-semibold
          focus:border-sky-500 focus:ring-2 focus:ring-sky-200
          outline-none transition"
      />
    </div>
  );
}
